'use client'

import FooterSection from '@/components/footer-section'
import { useLanguage } from '@/contexts/language-context'
import { translations } from '@/lib/translations'

interface PolicySection {
  number: number
  title: string
  content: string | string[]
}

function renderLine(line: string) {
  if (!line.includes('contact@getyesman.com')) return <>{line}</>
  return (
    <>
      {line.split('contact@getyesman.com').map((part, idx, arr) => (
        <span key={idx}>
          {part}
          {idx < arr.length - 1 && (
            <a href="mailto:contact@getyesman.com" title="Envoyer un email à YesMan" className="text-white underline hover:text-white/80 transition-colors">
              contact@getyesman.com
            </a>
          )}
        </span>
      ))}
    </>
  )
}

export default function PrivacyPage() {
  const { language } = useLanguage()
  const t = translations[language]
  const sections: PolicySection[] = [
    {
      number: 1,
      title: 'Introduction',
      content: 'YesMan est une plateforme de management opérationnel destinée aux PME africaines, couvrant les verticales BTP, Immobilier et Agriculture. Cette politique décrit comment nous collectons, utilisons, stockons et protégeons vos données via nos services, notamment via WhatsApp Business API. En utilisant YesMan, vous acceptez ces pratiques.',
    },
    {
      number: 2,
      title: 'Responsable du traitement',
      content: 'YesMan – ABIZZUP. RCCM Togo : TG-LOM-2020-B-1047. Siège : Lomé, Togo. Email : contact@getyesman.com',
    },
    {
      number: 3,
      title: 'Données collectées',
      content: [
        '3.1 Inscription et compte : Nom, email, téléphone WhatsApp, entreprise, fonction, facturation.',
        "3.2 Utilisation plateforme : Conversations WhatsApp, données clients importées, configurations workflows, statistiques YesScore, logs d'activité.",
        "3.3 Données opérationnelles chantier (BTP) : Photos horodatées (Bibliothèque de Preuves), données de présence et pointage, rapports d'avancement horodatés, données contrôle qualité, rapports d'incidents, journaux d'audit immutables, scores YesScore par intervenant.",
        '3.4 Données techniques : IP, navigateur, cookies, géolocalisation approximative.',
        '3.5 Données de paiement : facturation, transactions, méthodes de paiement (prestataires sécurisés).',
      ],
    },
    {
      number: 4,
      title: 'WhatsApp et services tiers',
      content: 'Utilisation de la WhatsApp Business API (Meta). Communications transitent par Meta. Acceptation des politiques Meta/WhatsApp requise.',
    },
    {
      number: 5,
      title: 'Finalités du traitement',
      content: "Fournir le service, assurer la traçabilité opérationelle des chantiers, facturation, support client, amélioration, communications (avec consentement), sécurité, obligations légales.",
    },
    {
      number: 6,
      title: 'Base légale',
      content: 'Exécution du contrat, consentement, intérêt légitime (sécurité, documentation probatoire), obligation légale.',
    },
    {
      number: 7,
      title: 'Responsabilité utilisateur',
      content: "Consentement préalable des destinataires WhatsApp, information des intervenants terrain (ouvriers, techniciens) sur la collecte de leurs données, interdiction de spam et d'usage abusif. Suspension possible en cas de non-respect.",
    },
    {
      number: 8,
      title: 'Partage des données',
      content: 'Fournisseurs cloud sécurisés, partenaires WhatsApp Business, prestataires de paiement. Transferts internationaux avec garanties contractuelles. Aucune vente de données.',
    },
    {
      number: 9,
      title: 'Durée de conservation',
      content: [
        '— Données compte : abonnement + 3 ans',
        '— Facturation : 10 ans',
        '— Conversations WhatsApp : 12 mois',
        '— Rapports horodatés / Bibliothèque de Preuves : 5 ans',
        "— Journaux d'audit (BlackBox) : 5 ans",
        '— Données présence et scores : 3 ans',
        '— Support : 2 ans',
        "— Marketing : jusqu'au retrait du consentement",
      ],
    },
    {
      number: 10,
      title: 'Sécurité',
      content: "Chiffrement transit et stockage, contrôle d'accès, surveillance des intrusions, sauvegardes, journaux d'audit immutables, procédures incident. Notification en cas de violation dans les délais légaux.",
    },
    {
      number: 11,
      title: 'Vos droits',
      content: 'Accès, rectification, suppression, limitation, portabilité, opposition, retrait du consentement. Contact : contact@getyesman.com. Délai : 30 jours.',
    },
    {
      number: 12,
      title: 'Cookies',
      content: 'Cookies essentiels + analytiques (avec consentement). Bannière de consentement à la première visite.',
    },
    {
      number: 13,
      title: 'Données des clients de nos utilisateurs',
      content: "YesMan agit en tant que sous-traitant. L'utilisateur est responsable de la collecte légale, du consentement et du respect des droits, y compris pour les intervenants terrain.",
    },
    {
      number: 14,
      title: 'Mineurs',
      content: 'Services non destinés aux mineurs. Suppression immédiate si collecte accidentelle.',
    },
    {
      number: 15,
      title: 'Modifications',
      content: 'Notification 30 jours avant toute modification substantielle.',
    },
    {
      number: 16,
      title: 'Loi applicable',
      content: 'Droit de la République Togolaise. Juridictions compétentes : Lomé.',
    },
    {
      number: 17,
      title: 'Contact',
      content: "contact@getyesman.com. Possibilité de contacter l'autorité de protection des données compétente dans votre pays.",
    },
  ]

  return (
    <div className="min-h-screen bg-black">
      <div className="relative bg-black px-8 py-24 md:py-32">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-4 tracking-tight leading-tight text-balance">
            {t.privacy.title}
          </h1>
          <p className="text-sm md:text-base text-white/50 font-mono uppercase tracking-wider">
            {t.privacy.updated}
          </p>
        </div>
      </div>

      <div className="relative bg-black px-8 pb-20">
        <div className="max-w-4xl mx-auto space-y-8">
          {sections.map((section) => (
            <div
              key={section.number}
              className="border border-white/10 bg-black/50 rounded-lg p-8 hover:border-white/20 transition-colors"
            >
              <div className="flex items-start gap-4 mb-4">
                <span className="inline-flex items-center justify-center w-10 h-10 bg-white/5 border border-white/20 rounded text-white/80 font-mono text-sm font-bold flex-shrink-0">
                  {section.number}
                </span>
                <h2 className="text-xl md:text-2xl font-bold text-white pt-1">
                  {section.title}
                </h2>
              </div>

              <div className="ml-14 space-y-4">
                {Array.isArray(section.content) ? (
                  section.content.map((line, idx) => (
                    <p key={idx} className="text-white/70 text-sm md:text-base leading-relaxed">
                      {renderLine(line)}
                    </p>
                  ))
                ) : (
                  <p className="text-white/70 text-sm md:text-base leading-relaxed">
                    {renderLine(section.content)}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <FooterSection />
    </div>
  )
}
